// POS UI location is stored in the hash (`#/layout-5/version-en_US/...`).
// Login can leave the page (social OAuth) or rewrite the URL (staff_access /
// auth_failed query cleanup, reloadNuxtApp). Fragments never reach the server,
// so keep a same-tab copy in sessionStorage and only restore hashes that look
// like POS navigation — never static-token fragments from /t.

const STORAGE_KEY = 'johku_pos_return_hash'
const TTL_MS = 30 * 60 * 1000

type SavedReturnHash = {
  shopId: string
  hash: string
  savedAt: number
}

export function isPosUiHash (hash: string | null | undefined): boolean {
  if (!hash || typeof hash !== 'string') {
    return false
  }
  const value = hash.startsWith('#') ? hash : '#' + hash
  return /^#\/layout-/.test(value) && !/[\r\n]/.test(value)
}

export function currentPosUiHash (): string {
  if (typeof window === 'undefined') {
    return ''
  }
  return isPosUiHash(window.location.hash) ? window.location.hash : ''
}

export function savePosReturnHash (shopId: string, hash = typeof window !== 'undefined' ? window.location.hash : '') {
  if (typeof window === 'undefined' || !shopId || !isPosUiHash(hash)) {
    return
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      shopId,
      hash,
      savedAt: Date.now()
    } satisfies SavedReturnHash))
  } catch {
    // sessionStorage can throw in private mode
  }
}

export function peekPosReturnHash (shopId: string): SavedReturnHash | null {
  if (typeof window === 'undefined' || !shopId) {
    return null
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const saved = JSON.parse(raw) as SavedReturnHash
    if (!saved || saved.shopId !== shopId || !isPosUiHash(saved.hash)) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (Date.now() - saved.savedAt > TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return saved
  } catch {
    return null
  }
}

export function clearPosReturnHash () {
  if (typeof window === 'undefined') {
    return
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function applyPosReturnHash (shopId: string): string {
  if (typeof window === 'undefined') {
    return ''
  }
  if (isPosUiHash(window.location.hash)) {
    clearPosReturnHash()
    return window.location.hash
  }
  const saved = peekPosReturnHash(shopId)
  if (!saved) {
    return ''
  }
  clearPosReturnHash()
  window.history.replaceState(null, '', window.location.pathname + window.location.search + saved.hash)
  return saved.hash
}

export function pathWithPosReturnHash (path: string, shopId: string): string {
  if (!path || path.includes('#')) {
    return path
  }
  const hash = currentPosUiHash() || peekPosReturnHash(shopId)?.hash || ''
  return hash ? path + hash : path
}
